import { z } from 'zod';
import { type ControlCatalogue } from './control-catalogue';
import { FrameworkIdSchema, FRAMEWORK_POLICY, type FrameworkId } from './framework-policy';
import { QrsArtefactIdSchema, QrsControlIdSchema } from './ids';
import { NonEmptyStringSchema } from './primitives';

/**
 * A standing, client-facing artefact stating what a framework deliberately does
 * **not** assess (docs/DECISIONS.md ZD-035).
 *
 * **It is authored content with its own identity, not a view assembled at
 * render time.** The obvious implementation is to walk the catalogue's unmapped
 * cells and print them, and it is wrong for a reason that is about the client
 * rather than about the code: a list of gaps, rendered without its framing, is
 * a list of criticisms of a certification a client is proud of. What makes the
 * artefact useful — *this is a scope clarification of a certificate that does
 * its own job well; you should come away better informed, not defensive* — is
 * an editorial judgement somebody made, and it has to travel with the content
 * rather than being re-supplied by whoever renders it next.
 *
 * That is the same distinction ZD-022 drew when it refused `ConflictGroup.summary`,
 * arrived at from the other side: a *summary* is an interpretation and must not
 * be generated by a deterministic worker; this framing is an interpretation and
 * must not be **lost** by one.
 *
 * **What is still checked against the data.** Every claim the artefact makes
 * names the controls it rests on, and `checkArtefactCorroboration` refuses a
 * claim whose controls do not actually carry a declared unmapped cell for that
 * framework. So the artefact cannot drift into saying a framework misses
 * something the catalogue maps, and the catalogue cannot quietly acquire a
 * mapping that contradicts the artefact. Authored, and corroborated.
 */

export const ScopeExclusionSchema = z.object({
  /** Short label — "Encryption in transit". */
  topic: NonEmptyStringSchema,
  /** What is outside scope, in the artefact's own client-facing wording. */
  detail: NonEmptyStringSchema,
  /**
   * The QRS controls this claim rests on. **Required and non-empty**: a claim
   * about what a framework does not assess, with nothing behind it, is exactly
   * the undeclared-absence problem the unmapped cells exist to fix, restated at
   * the level of a client-facing sentence.
   */
  related_control_ids: z.array(QrsControlIdSchema).min(1),
});
export type ScopeExclusion = z.infer<typeof ScopeExclusionSchema>;

export const FrameworkScopeArtefactSchema = z.object({
  artefact_id: QrsArtefactIdSchema,
  framework: FrameworkIdSchema,
  framework_version: NonEmptyStringSchema,
  title: NonEmptyStringSchema,
  /** Opening context — what the certificate is and who holds it. */
  preamble: NonEmptyStringSchema,
  /**
   * The editorial rule governing how this content is presented, carried **with**
   * the content. Required and non-empty: a framing rule that is optional is one
   * a renderer will eventually be built without.
   */
  framing_rule: NonEmptyStringSchema,
  does_not_assess: z.array(ScopeExclusionSchema).min(1),
  /**
   * What the framework *does* assess, in the artefact's own words. Required,
   * because a document that lists only what a certification misses is the
   * criticism the framing rule forbids, however carefully the rest is worded.
   */
  what_it_does_assess: NonEmptyStringSchema,
});
export type FrameworkScopeArtefact = z.infer<typeof FrameworkScopeArtefactSchema>;

export interface CorroborationFailure {
  topic: string;
  qrs_control_id: string;
  problem: 'unknown_control' | 'framework_is_mapped' | 'no_unmapped_declaration';
}

/**
 * Checks every claim in the artefact against the catalogue it describes.
 *
 * Returns the failures; empty means each claim rests on at least one control
 * that genuinely declares the framework unmapped.
 *
 * The three failure kinds are distinct on purpose. `unknown_control` is a stale
 * reference. `framework_is_mapped` is the dangerous one — the artefact telling a
 * client the framework does not cover something the catalogue says it does.
 * `no_unmapped_declaration` is a cell the catalogue itself has not declared —
 * unreachable against a schema-validated `exhaustive` catalogue, since
 * `ControlCatalogueSchema` refuses that state at the parse, but checked here
 * too rather than assumed: this function takes a `ControlCatalogue` value, not
 * a guarantee that it passed the schema, and a `targeted` or out-of-scope
 * framework can reach this branch honestly.
 */
export function checkArtefactCorroboration(
  artefact: FrameworkScopeArtefact,
  catalogue: ControlCatalogue,
): CorroborationFailure[] {
  const byId = new Map(catalogue.controls.map((control) => [control.qrs_control_id, control]));
  const failures: CorroborationFailure[] = [];

  for (const claim of artefact.does_not_assess) {
    const corroborating = claim.related_control_ids.filter((id) => {
      const control = byId.get(id);
      if (control === undefined) return false;
      return control.unmapped.some((cell) => cell.framework === artefact.framework);
    });
    if (corroborating.length > 0) continue;

    for (const id of claim.related_control_ids) {
      const control = byId.get(id);
      if (control === undefined) {
        failures.push({ topic: claim.topic, qrs_control_id: id, problem: 'unknown_control' });
      } else if (control.framework_mappings.some((row) => row.framework === artefact.framework)) {
        failures.push({ topic: claim.topic, qrs_control_id: id, problem: 'framework_is_mapped' });
      } else {
        failures.push({
          topic: claim.topic,
          qrs_control_id: id,
          problem: 'no_unmapped_declaration',
        });
      }
    }
  }
  return failures;
}

/** The artefact may only describe a framework the policy permits us to name. */
export function assertNameableFramework(framework: FrameworkId): void {
  if (FRAMEWORK_POLICY[framework].status !== 'permitted') {
    throw new Error(
      `${FRAMEWORK_POLICY[framework].display_name} is a rejected framework and may not be the ` +
        `subject of a client-facing artefact`,
    );
  }
}
